// src/components/Pentests/tabs/tools/CustomScanConfig.js
//
// Inline panel that renders below the ProfileCombobox when "Custom" is picked.
// Operator picks categories, optional tags, and (optionally) tweaks rate
// limit / concurrency / DAST mode in the Advanced fold-out.
//
// State is owned by the parent (VulnScanLauncher); this is a controlled view.

import { ChevronDown, ChevronRight, Plus, Settings2, Tag, X } from 'lucide-react';
import { useState } from 'react';

function CategoryPill({ cat, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(cat.name)}
      className={`group text-left rounded-lg border px-3 py-2 transition ${
        selected
          ? 'border-shinobi-green bg-wasabi-100 ring-1 ring-shinobi-green/40'
          : 'border-cyber-300/60 bg-cyber-100 hover:border-cyber-300'
      }`}
    >
      <div className={`text-sm font-semibold ${selected ? 'text-shinobi-green' : 'text-slate-900'}`}>
        {cat.label}
      </div>
      {cat.description && (
        <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{cat.description}</div>
      )}
    </button>
  );
}

function TagChip({ tag, selected, onToggle, onRemove, custom = false }) {
  return (
    <span
      onClick={selected ? undefined : () => onToggle(tag)}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border transition ${
        selected
          ? 'bg-wasabi-100 text-wasabi-700 border-wasabi-300'
          : 'bg-cyber-100 text-slate-700 border-cyber-300/60 hover:border-cyber-300 cursor-pointer'
      }`}
    >
      {custom && <Tag className="w-3 h-3" />}
      <span>{tag}</span>
      {selected && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(tag); }}
          className="hover:text-rose-700"
          aria-label={`remove ${tag}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

function Slider({ label, value, min, max, step = 1, onChange, suffix }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-700">{label}</span>
        <span className="text-xs font-mono text-shinobi-green">{value}{suffix ? ` ${suffix}` : ''}</span>
      </div>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-shinobi-green"
      />
    </label>
  );
}

export default function CustomScanConfig({
  options,
  categories,
  tags,
  rateLimit,
  concurrency,
  dastMode,
  onChangeCategories,
  onChangeTags,
  onChangeRateLimit,
  onChangeConcurrency,
  onChangeDastMode,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const cats = options?.categories || [];
  const commonTags = options?.common_tags || [];

  const toggleCategory = (name) => {
    onChangeCategories(
      categories.includes(name)
        ? categories.filter((c) => c !== name)
        : [...categories, name],
    );
  };

  const addTag = (tag) => {
    const t = (tag || '').trim().toLowerCase();
    if (!t) return;
    if (tags.includes(t)) return;
    onChangeTags([...tags, t]);
  };

  const removeTag = (tag) => {
    onChangeTags(tags.filter((t) => t !== tag));
  };

  const onTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
      setTagInput('');
    }
  };

  return (
    <div className="rounded-xl border border-wasabi-300 bg-cyber-50/60 p-4 space-y-5">
      <div className="flex items-center gap-2 text-shinobi-green">
        <Settings2 className="w-4 h-4" />
        <span className="text-sm font-semibold uppercase tracking-wider">Custom configuration</span>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-sm font-medium text-slate-800">Categories</label>
          <span className="text-[11px] text-slate-500">at least one required</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {cats.map((cat) => (
            <CategoryPill
              key={cat.name}
              cat={cat}
              selected={categories.includes(cat.name)}
              onToggle={toggleCategory}
            />
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-sm font-medium text-slate-800">Tags</label>
          <span className="text-[11px] text-slate-500">optional</span>
        </div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((t) => (
              <TagChip
                key={t}
                tag={t}
                selected
                onToggle={() => {}}
                onRemove={removeTag}
                custom={!commonTags.includes(t)}
              />
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {commonTags
            .filter((t) => !tags.includes(t))
            .map((t) => (
              <TagChip key={t} tag={t} selected={false} onToggle={addTag} onRemove={removeTag} />
            ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={onTagInputKeyDown}
            placeholder="Add custom tag and press Enter"
            className="flex-1 bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-1.5 text-xs text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
          <button
            type="button"
            onClick={() => { addTag(tagInput); setTagInput(''); }}
            disabled={!tagInput.trim()}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-cyber-300/60 text-xs text-slate-700 hover:border-shinobi-green/70 hover:text-shinobi-green disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setAdvancedOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"
        >
          {advancedOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Advanced
        </button>
        {advancedOpen && (
          <div className="mt-3 grid sm:grid-cols-2 gap-4 pl-5 border-l border-cyber-300/40">
            <Slider
              label="Rate limit"
              value={rateLimit}
              min={1}
              max={500}
              onChange={onChangeRateLimit}
              suffix="req/s"
            />
            <Slider
              label="Concurrency"
              value={concurrency}
              min={1}
              max={50}
              onChange={onChangeConcurrency}
            />
            <label className="flex items-center justify-between sm:col-span-2 bg-cyber-100/40 border border-cyber-300/60 rounded-lg px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-800">DAST mode</div>
                <div className="text-[11px] text-slate-500">two-pass run merging standard + DAST templates</div>
              </div>
              <input
                type="checkbox"
                checked={dastMode}
                onChange={(e) => onChangeDastMode(e.target.checked)}
                className="w-4 h-4 accent-shinobi-green"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  );
}
